import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const HIGHLIGHTS = [
  {
    id: 'men',
    altKey: 'collections.highlight_men.alt',
    position: 'left',
  },
  {
    id: 'bags',
    altKey: 'collections.highlight_bags.alt',
    position: 'right',
  },
];

const pickBannerWithImage = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return source.find((banner) => Boolean(banner?.image)) || null;
};

const getRedirectUrl = (banner) => {
  if (!banner) {
    return '';
  }
  return banner.redirectUrl ?? banner.redirect_url ?? banner.url ?? '';
};

export const normalizeCategories = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return source
    .map((item, index) => ({
      id: item.categoryId ?? item.id ?? `category-${index}`,
      name: item.name || item.title || '',
      image: item.image,
      priority: item.priority ?? index,
    }))
    .filter((item, index) => !source[index]?.hide) // hidden in the dashboard
    .sort((a, b) => a.priority - b.priority);
};

const CollectionsSection = () => {
  const [categories, setCategories] = useState([]);
  const [highlightBanners, setHighlightBanners] = useState({
    left: null,
    right: null,
  });
  const { t } = useLanguage();

  useEffect(() => {
    let mounted = true;

    Api.fetchCategories()
      .then((data) => {
        if (!mounted) return;
        const normalized = normalizeCategories(data);
        setCategories(normalized);
      })
      .catch(() => {
        if (mounted) setCategories([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    Promise.all([Api.fetchMiddleDoubleLeftBanner(), Api.fetchMiddleDoubleRightBanner()])
      .then(([left, right]) => {
        if (!mounted) return;
        setHighlightBanners({
          left: pickBannerWithImage(left),
          right: pickBannerWithImage(right),
        });
      })
      .catch(() => {
        if (!mounted) return;
        setHighlightBanners({ left: null, right: null });
      });

    return () => {
      mounted = false;
    };
  }, []);

  const visibleCategories = useMemo(
    () => categories.slice(0, 8),
    [categories]
  );

  const highlightCards = useMemo(
    () =>
      HIGHLIGHTS.map((card) => {
        const dynamicBanner =
          card.position === 'left'
            ? highlightBanners.left
            : highlightBanners.right;

        if (!dynamicBanner?.image) {
          return null;
        }

        return {
          id: card.id,
          imageSrc: dynamicBanner.image,
          altText:
            dynamicBanner.title ||
            dynamicBanner.description ||
            t(card.altKey),
          redirectUrl: getRedirectUrl(dynamicBanner),
        };
      }).filter(Boolean),
    [highlightBanners, t]
  );

  const navigateTo = (url) => {
    if (!url) return;
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
  };

  const handleKeyPress = (event, url) => {
    if (!url) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateTo(url);
    }
  };

  return (
    <section className="collections">
      <div className="section-heading">
        <h2>{t('collections.title')}</h2>
      </div>

      <div className="collections__grid">
        {visibleCategories.map((category) => (
          <button
            key={category.id}
            type="button"
            className="collections__item"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.assign(
                  `/products?category=${encodeURIComponent(category.id)}`
                );
              }
            }}
          >
            <div className="collections__item-image">
              <img
                src={category.image}
                alt={category.name}
                loading="lazy"
                onError={(event) => {
                 
                }}
              />
            </div>
            <p>{category.name || t('collections.fallback_category')}</p>
          </button>
        ))}
      </div>

      {highlightCards.length > 0 && (
        <div className="collections__highlights">
          {highlightCards.map((card) => (
            <article
              key={card.id}
              className="collections__card"
              role={card.redirectUrl ? 'button' : undefined}
              tabIndex={card.redirectUrl ? 0 : undefined}
              onClick={
                card.redirectUrl ? () => navigateTo(card.redirectUrl) : undefined
              }
              onKeyPress={
                card.redirectUrl
                  ? (event) => handleKeyPress(event, card.redirectUrl)
                  : undefined
              }
            >
              <img src={card.imageSrc} alt={card.altText} loading="lazy" />
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default CollectionsSection;
